import { useProvider } from "../lib/providersManifest";

/**
 * Iter-185.BK — Trust signal stack (AUDIT FINDING #11).
 * Iter-190.I — Typography pass.
 *
 * Compact third-party trust-signal badges rendered next to the
 * carrier name on insurance rate tables. Surfaces:
 *
 *   - J.D. Power 2024 customer satisfaction score (0-1000)
 *   - NAIC complaint index (1.0 = industry average; <1.0 = fewer
 *     complaints; >1.0 = more complaints)
 *
 * Iter-190.I — Stripped the chunky bordered-chip styling that
 * stacked these badges vertically under the AM Best chip and made
 * the table feel cluttered. Both badges now render as plain inline
 * typography that composes with `AmBestBadge` inside the parent
 * `flex flex-wrap` container in PartnerRateTable. The parent uses a
 * `·` sibling-dot CSS rule so the row reads as a single typography-
 * driven trust strip:
 *
 *     AM Best A++ · Superior  ·  J.D. Power 2024 873  ·  NAIC 0.65
 *
 * Data source:
 *   - providers.trust_signals.jd_power_score (int 0-1000)
 *   - providers.trust_signals.jd_power_year (int, e.g. 2024)
 *   - providers.trust_signals.naic_complaint_index (float)
 *   - providers.trust_signals.naic_period (string, e.g. "2023-2024")
 *
 * Renders null when no signals are set so it's safe to drop into
 * any row — zero layout shift on uncovered carriers.
 */

function jdPowerTone(score) {
  // J.D. Power scale: 0-1000. Industry avg ~850 for auto insurance.
  if (score >= 880) return "text-emerald-700";
  if (score >= 840) return "text-emerald-700";
  if (score >= 800) return "text-slate-700";
  return "text-amber-700";
}

function naicTone(index) {
  // NAIC complaint index: 1.0 = industry average. <1 better, >1 worse.
  if (index < 0.7) return "text-emerald-700";
  if (index < 1.0) return "text-emerald-700";
  if (index <= 1.5) return "text-slate-700";
  return "text-amber-700";
}

export function JdPowerBadge({ slug, size = "sm" }) {
  void size;
  const provider = useProvider(slug);
  const ts = provider?.trust_signals || {};
  const score = ts.jd_power_score;
  if (!score) return null;

  const year = ts.jd_power_year || 2024;
  const tone = jdPowerTone(score);

  return (
    <span
      data-testid={`jd-power-badge-${slug}`}
      title={`J.D. Power ${year} U.S. Auto Insurance Study customer satisfaction score: ${score}/1000. Source: jdpower.com.`}
      className={`inline-flex items-baseline whitespace-nowrap ${tone}`}
    >
      <span className="opacity-60">J.D. Power {year}</span>
      <span
        data-testid={`jd-power-score-${slug}`}
        className="ml-1 font-display tabular-nums"
      >
        {score}
      </span>
    </span>
  );
}

export function NaicComplaintBadge({ slug, size = "sm" }) {
  void size;
  const provider = useProvider(slug);
  const ts = provider?.trust_signals || {};
  const index = ts.naic_complaint_index;
  if (index === undefined || index === null) return null;

  const period = ts.naic_period || "2023";
  const tone = naicTone(index);

  return (
    <span
      data-testid={`naic-badge-${slug}`}
      title={`NAIC complaint index ${index} (${period}). Industry average = 1.0; lower is better. Source: naic.org Market Conduct.`}
      className={`inline-flex items-baseline whitespace-nowrap ${tone}`}
    >
      <span className="opacity-60">NAIC</span>
      <span
        data-testid={`naic-index-${slug}`}
        className="ml-1 font-display tabular-nums"
      >
        {Number(index).toFixed(2)}
      </span>
    </span>
  );
}

/**
 * Convenience wrapper that renders both trust badges in a single
 * inline flex row with `·` sibling separators. Used by
 * PartnerRateTable beside `<AmBestBadge>` in the carrier-name cell.
 *
 * Iter-190.I — Uses Tailwind's arbitrary-selector pseudo-element
 * to inject a faint `·` between siblings — no extra DOM nodes,
 * no JSX gymnastics.
 */
export default function TrustBadgeStack({ slug, size = "sm" }) {
  void size;
  return (
    <span
      data-testid={`trust-badges-${slug}`}
      className="inline-flex flex-wrap items-baseline gap-x-2 gap-y-0 [&>*+*]:before:content-['·'] [&>*+*]:before:text-slate-300 [&>*+*]:before:mr-2 [&>*+*]:before:font-medium"
    >
      <JdPowerBadge slug={slug} size={size} />
      <NaicComplaintBadge slug={slug} size={size} />
    </span>
  );
}
